<script setup lang="ts">
import { SliderRange, SliderRoot, SliderThumb, SliderTrack } from "reka-ui"
import { computed } from "vue"

const props = withDefaults(defineProps<{
  modelValue: number
  min?: number
  max: number
  step?: number
  disabled?: boolean
  ariaLabel?: string
  dataTest?: string
  valueFormatter?: (value: number) => string
}>(), {
  min: 0,
  step: 1,
  disabled: false,
  ariaLabel: "Slider",
})

const emit = defineEmits<{
  (e: "update:modelValue", value: number): void
}>()

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

const rekaValue = computed<number[]>({
  get: () => [clamp(props.modelValue, props.min, props.max)],
  set: (arr) => {
    const raw = arr?.[0]
    if (typeof raw !== "number" || Number.isNaN(raw))
      return
    emit("update:modelValue", clamp(raw, props.min, props.max))
  },
})

const formattedValue = computed(() => {
  const v = clamp(props.modelValue, props.min, props.max)
  return props.valueFormatter ? props.valueFormatter(v) : String(v)
})
</script>

<template>
  <div class="flex items-center gap-2" data-test="slider">
    <slot name="icon" />
    <SliderRoot
      v-model="rekaValue"
      :min="min"
      :max="max"
      :step="step"
      :disabled="disabled"
      class="group relative flex flex-1 items-center select-none touch-none h-5"
      :data-test="dataTest"
      :aria-label="ariaLabel"
    >
      <SliderTrack
        class="relative grow rounded-full h-1.5 overflow-hidden bg-gray-300 group-data-[disabled=true]:opacity-70"
      >
        <SliderRange
          class="absolute h-full rounded-full bg-[var(--template-primary-colour)]"
        />
      </SliderTrack>

      <SliderThumb
        class="block w-[18px] h-[18px] rounded-full bg-[var(--template-primary-colour)] border-2 border-white shadow-md transition-all duration-200 ease-in-out cursor-pointer hover:scale-110 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-black/15 group-data-[disabled=true]:cursor-not-allowed group-data-[disabled=true]:opacity-70"
      />
    </SliderRoot>

    <slot name="right">
      <span
        class="text-sm text-gray-600 font-medium w-[3.5rem] text-right shrink-0"
        data-test="slider-value"
      >
        {{ formattedValue }}
      </span>
    </slot>
  </div>
</template>
